import React, { useState } from 'react';
import { V } from '../../utils/theme';
import { Card } from '../../components/ui';
import { apiPost, ApiError } from '../../utils/api';
import { IRON_RANKS } from '../../data/ranks';
import { useLayout } from '../../utils/responsive';

export function AdminXPManager({s}){
  const { isDesktop } = useLayout();
  const email=s.profile?.email;
  // apiPost reads the session token fresh on every call, so no stale-closure 401 risk
  const af=async(action,extra={})=>{
    try{return await apiPost("/api/admin",{action,...extra},{email});}
    catch(e){return{__error:e instanceof ApiError?(e.body?.error||`HTTP ${e.status}`):e.message};}
  };

  const [users,setUsers]=useState(null);
  const [usersLoading,setUsersLoading]=useState(false);
  const [search,setSearch]=useState("");
  const [selUser,setSelUser]=useState(null);
  const [gData,setGData]=useState(null); // {gamification, workouts_count}
  const [gLoading,setGLoading]=useState(false);
  const [gError,setGError]=useState(null);

  // XP adjustment
  const [xpAmount,setXpAmount]=useState("");
  const [xpReason,setXpReason]=useState("");
  const [xpResult,setXpResult]=useState(null);
  const [xpSaving,setXpSaving]=useState(false);

  // Rank override
  const [targetRank,setTargetRank]=useState("");
  const [rankResult,setRankResult]=useState(null);
  const [rankSaving,setRankSaving]=useState(false);

  const loadUsers=()=>{
    setUsersLoading(true);
    af("users").then(r=>{setUsers(r?.users||[]);setUsersLoading(false);}).catch(()=>setUsersLoading(false));
  };

  const selectUser=(u)=>{
    setSelUser(u);setGData(null);setXpAmount("");setXpReason("");setXpResult(null);setRankResult(null);setTargetRank("");
    setGLoading(true);setGError(null);
    Promise.all([af("get_gamification",{email:u.email}),af("user_detail",{email:u.email})]).then(([gRes,dRes])=>{
      // af() returns null on failure — surface it instead of rendering an empty XP panel
      if(!gRes&&!dRes){setGError("Failed to load user — check session and try again");setGLoading(false);return;}
      const g=gRes?.gamification||{};
      setGData({g,workouts:dRes?.workouts?.length||0,nutrition:dRes?.nutrition?.length||0,checkins:dRes?.checkins?.length||0,photos:dRes?.photos?.length||0});
      setGLoading(false);
    }).catch(()=>setGLoading(false));
  };

  const applyXP=async()=>{
    const amt=parseInt(xpAmount);
    if(!selUser||isNaN(amt))return;
    setXpSaving(true);setXpResult(null);
    const res=await af("adjust_xp",{email:selUser.email,amount:amt,reason:xpReason.trim()||"Admin adjustment"});
    if(res?.success){
      setXpResult({ok:true,msg:`Done. New bonus XP total: ${(res.new_total||0).toLocaleString()}`});
      af("get_gamification",{email:selUser.email}).then(r=>{if(r?.gamification)setGData(d=>({...d,g:r.gamification}));});
      setXpAmount("");setXpReason("");
    }else setXpResult({ok:false,msg:res?.__error||"Failed — check session"});
    setXpSaving(false);
  };

  const applyRankOverride=async()=>{
    if(!selUser||!targetRank)return;
    const rank=IRON_RANKS.find(r=>r.level===parseInt(targetRank));
    if(!rank)return;
    setRankSaving(true);setRankResult(null);
    const g=gData?.g||{};
    // Calculate how much bonus XP to set so total hits the rank threshold
    // baseXP is workouts*30 + nutrition*8 etc — we estimate from what we have
    const estBase=(gData?.workouts||0)*30+(gData?.nutrition||0)*8+(gData?.checkins||0)*5+(gData?.photos||0)*15;
    const neededBonus=Math.max(0,rank.xpNeeded-estBase);
    const currentBonus=g.xp_bonus?.total||0;
    const delta=neededBonus-currentBonus;
    const reason=`Admin rank override → ${rank.icon} ${rank.name}`;
    const res=await af("adjust_xp",{email:selUser.email,amount:delta,reason});
    // Also update last_known_level so level-up doesn't re-trigger incorrectly
    if(res?.success){
      await af("set_notif_flags",{email:selUser.email,flags:{}}); // no-op to ensure settings row exists
      // Update last_known_level via adjust (already set in gamification upsert above via adjust_xp)
      setRankResult({ok:true,msg:`${selUser.name||selUser.email.split("@")[0]} is now ${rank.icon} ${rank.name}`});
      af("get_gamification",{email:selUser.email}).then(r=>{if(r?.gamification)setGData(d=>({...d,g:r.gamification}));});
    }else setRankResult({ok:false,msg:res?.__error||"Failed — check session"});
    setRankSaving(false);
  };

  const filteredUsers=(users||[]).filter(u=>!search||u.name?.toLowerCase().includes(search.toLowerCase())||u.email?.toLowerCase().includes(search.toLowerCase()));

  const currentXP=(()=>{
    if(!gData)return null;
    const g=gData.g;
    const bonus=g?.xp_bonus?.total||0;
    const est=(gData.workouts||0)*30+(gData.nutrition||0)*8+(gData.checkins||0)*5+(gData.photos||0)*15;
    return est+bonus;
  })();
  const currentRank=currentXP!=null?([...IRON_RANKS].reverse().find(r=>currentXP>=r.xpNeeded)||IRON_RANKS[0]):null;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div>
        <div style={{fontSize:16,fontWeight:800,color:V.text}}>⚡ XP & Rank Manager</div>
        <div style={{fontSize:11,color:V.text3}}>View gamification stats, adjust XP, and override ranks for any user</div>
      </div>

      {/* User selector */}
      <Card style={{padding:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div style={{fontSize:12,fontWeight:700,color:V.text}}>Select User</div>
          {users===null?(
            <button onClick={loadUsers} style={{padding:"6px 14px",borderRadius:8,background:V.accent,border:"none",
              cursor:"pointer",fontSize:11,fontWeight:700,color:V.bg,fontFamily:V.font}}>
              {usersLoading?"Loading…":"Load Users"}
            </button>
          ):(
            <div style={{fontSize:10,color:V.text3}}>{users.length} users</div>
          )}
        </div>
        {users!==null&&(
          <>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name or email…"
              style={{width:"100%",padding:"8px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
                borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.font,boxSizing:"border-box",marginBottom:6}}/>
            <div style={{maxHeight:180,overflowY:"auto",display:"flex",flexDirection:"column",gap:3}}>
              {filteredUsers.map(u=>{
                const sel=selUser?.email===u.email;
                return(
                  <div key={u.email} onClick={()=>selectUser(u)}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,cursor:"pointer",
                      background:sel?`${V.accent}12`:V.card,
                      border:`1px solid ${sel?V.accent+"40":V.cardBorder}`,transition:"all .1s"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:sel?700:400,color:sel?V.text:V.text3}}>{u.name||u.email.split("@")[0]}</div>
                      <div style={{fontSize:9,color:V.text3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.email}</div>
                    </div>
                    {sel&&<span style={{fontSize:10,color:V.accent}}>✓</span>}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>

      {/* Gamification stats */}
      {selUser&&(
        <Card style={{padding:14,border:`1px solid ${currentRank?currentRank.color+"20":V.cardBorder}`,
          background:currentRank?`${currentRank.color}04`:"transparent"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:700,color:V.text}}>{selUser.name||selUser.email.split("@")[0]}</div>
            {currentRank&&(
              <div style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:8,
                background:`${currentRank.color}12`,border:`1px solid ${currentRank.color}30`}}>
                <span style={{fontSize:16}}>{currentRank.icon}</span>
                <span style={{fontSize:11,fontWeight:800,color:currentRank.color}}>{currentRank.name}</span>
              </div>
            )}
          </div>
          {gLoading&&<div style={{fontSize:11,color:V.text3,textAlign:"center",padding:16}}>Loading…</div>}
          {!gLoading&&gError&&(
            <div style={{fontSize:11,color:V.danger,textAlign:"center",padding:16,border:`1px solid ${V.danger}40`,borderRadius:8}}>{gError}</div>
          )}
          {!gLoading&&gData&&(()=>{
            const g=gData.g||{};
            const bonus=g.xp_bonus?.total||0;
            const shields=g.streak_shields||0;
            const badges=Object.keys(g.badge_dates||{}).length;
            const missionDays=Object.keys(g.missions_completed||{}).length;
            return(
              <>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6,marginBottom:10}}>
                  {[
                    {l:"Total XP",v:(currentXP||0).toLocaleString(),c:V.accent},
                    {l:"Bonus XP",v:bonus.toLocaleString(),c:"#f59e0b"},
                    {l:"Level",v:currentRank?.level||1,c:currentRank?.color||V.text},
                    {l:"Shields",v:shields,c:"#22c55e"},
                    {l:"Badges",v:badges,c:"#f59e0b"},
                    {l:"Mission Days",v:missionDays,c:V.accent2},
                    {l:"Workouts",v:gData.workouts,c:V.accent},
                    {l:"War Wins",v:g.war_wins||0,c:"#f43f5e"},
                  ].map(st=>(
                    <div key={st.l} style={{textAlign:"center",padding:8,borderRadius:8,background:V.card}}>
                      <div style={{fontSize:14,fontWeight:800,color:st.c,fontFamily:V.mono}}>{st.v}</div>
                      <div style={{fontSize:8,color:V.text3,marginTop:2}}>{st.l}</div>
                    </div>
                  ))}
                </div>
                {/* XP bonus log */}
                {g.xp_bonus?.log?.length>0&&(
                  <div style={{maxHeight:80,overflowY:"auto",marginBottom:4}}>
                    <div style={{fontSize:9,fontWeight:700,color:V.text3,marginBottom:4,textTransform:"uppercase",letterSpacing:".06em"}}>Recent XP Log</div>
                    {g.xp_bonus.log.slice(0,8).map((e2,i)=>(
                      <div key={i} style={{fontSize:9,color:V.text3,padding:"2px 0",borderBottom:`1px solid ${V.divider}`}}>
                        {e2.date} · <span style={{color:e2.amount>=0?V.accent:V.danger,fontWeight:700}}>{e2.amount>=0?"+":""}{e2.amount}</span> · {e2.reason}
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </Card>
      )}

      {/* XP Adjustment */}
      {selUser&&!gLoading&&(
        <Card style={{padding:14}}>
          <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:10}}>Adjust XP</div>
          <div style={{display:"flex",gap:8,marginBottom:8}}>
            <input type="number" value={xpAmount} onChange={e=>setXpAmount(e.target.value)} placeholder="Amount (+/-)"
              style={{flex:1,padding:"10px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
                borderRadius:8,color:V.text,fontSize:13,outline:"none",fontFamily:V.mono,boxSizing:"border-box"}}/>
            <div style={{display:"flex",gap:4}}>
              {[100,500,1000,-100,-500].map(n=>(
                <button key={n} onClick={()=>setXpAmount(String(n))}
                  style={{padding:"8px 8px",borderRadius:6,border:`1px solid ${n>0?V.accent+"30":V.danger+"30"}`,
                    background:n>0?`${V.accent}08`:`${V.danger}08`,cursor:"pointer",fontSize:9,fontWeight:700,
                    color:n>0?V.accent:V.danger,fontFamily:V.mono,flexShrink:0}}>
                  {n>0?"+":""}{n}
                </button>
              ))}
            </div>
          </div>
          <input value={xpReason} onChange={e=>setXpReason(e.target.value)} placeholder="Reason (optional)"
            style={{width:"100%",padding:"10px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
              borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.font,boxSizing:"border-box",marginBottom:8}}/>
          {xpResult&&(
            <div style={{padding:"8px 12px",borderRadius:8,marginBottom:8,
              background:xpResult.ok?`${V.accent}10`:`${V.danger}10`,
              border:`1px solid ${xpResult.ok?V.accent+"30":V.danger+"30"}`,
              fontSize:11,color:xpResult.ok?V.accent:V.danger,fontWeight:600}}>
              {xpResult.ok?"✅ ":"❌ "}{xpResult.msg}
            </div>
          )}
          <button onClick={applyXP} disabled={!xpAmount||xpSaving}
            style={{width:"100%",padding:"11px",borderRadius:10,border:"none",cursor:"pointer",fontFamily:V.font,
              fontSize:12,fontWeight:800,
              background:xpAmount?`linear-gradient(135deg,${V.accent},${V.accent2})`:V.hoverBg,
              color:xpAmount?V.bg:V.text3}}>
            {xpSaving?"Applying…":`Apply ${xpAmount?(parseInt(xpAmount)>0?"+":"")+xpAmount+" XP":""}`}
          </button>
        </Card>
      )}

      {/* Rank Override */}
      {selUser&&!gLoading&&(
        <Card style={{padding:14}}>
          <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:10}}>Override Rank</div>
          <div style={{display:"grid",gridTemplateColumns:isDesktop?"1fr 1fr 1fr":"1fr 1fr",gap:6,marginBottom:10}}>
            {IRON_RANKS.map(r=>(
              <div key={r.level} onClick={()=>setTargetRank(String(r.level))}
                style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:8,cursor:"pointer",
                  border:`1px solid ${targetRank===String(r.level)?r.color+"60":V.cardBorder}`,
                  background:targetRank===String(r.level)?`${r.color}10`:V.card,
                  transition:"all .1s"}}>
                <span style={{fontSize:16}}>{r.icon}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:targetRank===String(r.level)?700:400,color:targetRank===String(r.level)?r.color:V.text3,lineHeight:1.2}}>{r.name}</div>
                  <div style={{fontSize:8,color:V.text3}}>{r.xpNeeded.toLocaleString()} XP</div>
                </div>
                {currentRank?.level===r.level&&<span style={{fontSize:8,color:r.color,fontWeight:700}}>NOW</span>}
              </div>
            ))}
          </div>
          {rankResult&&(
            <div style={{padding:"8px 12px",borderRadius:8,marginBottom:8,
              background:rankResult.ok?`${V.accent}10`:`${V.danger}10`,
              border:`1px solid ${rankResult.ok?V.accent+"30":V.danger+"30"}`,
              fontSize:11,color:rankResult.ok?V.accent:V.danger,fontWeight:600}}>
              {rankResult.ok?"✅ ":"❌ "}{rankResult.msg}
            </div>
          )}
          <button onClick={applyRankOverride} disabled={!targetRank||rankSaving}
            style={{width:"100%",padding:"11px",borderRadius:10,border:"none",cursor:"pointer",fontFamily:V.font,
              fontSize:12,fontWeight:800,
              background:targetRank?`linear-gradient(135deg,#f59e0b,#f97316)`:V.hoverBg,
              color:targetRank?V.bg:V.text3}}>
            {rankSaving?"Applying…":targetRank?`Set to ${IRON_RANKS.find(r=>r.level===parseInt(targetRank))?.icon} ${IRON_RANKS.find(r=>r.level===parseInt(targetRank))?.name}`:"Select a Rank"}
          </button>
          <div style={{fontSize:9,color:V.text3,textAlign:"center",marginTop:6,lineHeight:1.4}}>
            Rank override adjusts bonus XP so total XP hits that rank's threshold.<br/>Takes effect next time the user opens the app.
          </div>
        </Card>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// MOCK DATA ENGINE — localStorage only, never syncs to API
// Key: "ft-mock-analytics" — clear via clearMock() only
// ═══════════════════════════════════════════════════════
